import qs from 'qs';
import { AxiosResponse } from 'axios';
import { getAPI } from 'requests/api';
import { resolveDisciplineStatus } from './discipline-status';
import type {
  EvaluationKind,
  IClassroomEnrollment,
  IClassroomEvaluation
} from 'interfaces/classrooms';
import type { IAcademicRecord } from 'interfaces/academic_records';
import type { IAssessment, IAssessmentAttempt } from 'interfaces/assessments';
import type { IAssignmentAttempt } from 'interfaces/assignments';

export interface IClassroomsEnrollmentsData extends IClassroomEnrollment {
  academic_record_score?: IAcademicRecord['score'];
  academic_record_attendance_rate?: IAcademicRecord['attendance_rate'];
  academic_record_status?:
    | IAcademicRecord['status']
    | 'progress_failed'
    | 'in_progress';
}

export const EVALUATION_KIND_MAP = {
  objective: 'Objetiva',
  discursive: 'Discursiva',
  mixed: 'Mista',
  assignment: 'Atividade'
};

export const fetchClassroomsEnrollments = async (
  courseEnrollmentId: string,
  fetchType: 'Course' | 'GraduateCourse'
): Promise<IClassroomsEnrollmentsData[]> => {
  const [classroomsEnrollments] = await Promise.all([
    getAPI().get(
      `/admin/classrooms/enrollments?items=100&page=1&filter[by_course_enrollment_id]=${courseEnrollmentId}${
        fetchType === 'Course'
          ? '&filter[by_course_enrollment_type]=Courses::Enrollment'
          : ''
      }`
    )
  ]);

  return mountClassroomsEnrollmentsData(classroomsEnrollments.data);
};

export const mountClassroomsEnrollmentsData = (
  classroomsEnrollments: IClassroomEnrollment[]
): IClassroomsEnrollmentsData[] => {
  return classroomsEnrollments.map(classroomEnrollment => {
    const { status: academic_record_status } =
      resolveDisciplineStatus(classroomEnrollment);
    let academic_record_score: number | undefined;

    if (classroomEnrollment.academic_record) {
      academic_record_score = classroomEnrollment.academic_record.score;
    } else if (classroomEnrollment.approved !== null) {
      academic_record_score = classroomEnrollment.score;
    }

    const academic_record_attendance_rate =
      classroomEnrollment.academic_record?.attendance_rate ??
      classroomEnrollment.attendance_rate;

    return {
      ...classroomEnrollment,
      academic_record_score,
      academic_record_attendance_rate,
      academic_record_status
    };
  });
};

export interface IEvaluationsWithAttemptsData extends IClassroomEvaluation {
  score: number;
  attempts: (IAssessmentAttempt | IAssignmentAttempt)[];
  evaluationKind: EvaluationKind;
}

export const fetchEvaluations = async (
  classroomId: string,
  classroomEnrollmentId: string
): Promise<IEvaluationsWithAttemptsData[]> => {
  const evaluations = await getAPI().get(
    `/admin/classrooms/evaluations?filter[by_classroom_id]=${classroomId}`
  );

  if (evaluations?.data?.length > 0) {
    const { assessmentAttempts, assignmentAttempts } = await fetchAttempts(
      evaluations.data,
      classroomEnrollmentId
    );

    return mountEvaluationsData(
      evaluations.data,
      assessmentAttempts,
      assignmentAttempts
    );
  }

  return [];
};

export const fetchAttempts = async (
  evaluations: IClassroomEvaluation[],
  classroomEnrollmentId: string
) => {
  const { assessmentIds, assignmentIds } =
    getEvaluationsIdsByAttemptType(evaluations);

  const [assessmentAttempts, assignmentAttempts] = await Promise.all([
    assessmentIds.length > 0
      ? fetchAssessmentsAttempts(assessmentIds, classroomEnrollmentId)
      : Promise.resolve(),
    assignmentIds.length > 0
      ? fetchAssignmentsAttempts(assignmentIds, classroomEnrollmentId)
      : Promise.resolve()
  ]);

  return {
    assessmentAttempts: assessmentAttempts?.data || [],
    assignmentAttempts: assignmentAttempts?.data || []
  };
};

const fetchAssessmentsAttempts = async (
  assessmentIds: string[],
  classroomEnrollmentId: string
) => {
  const assessmentUrl = `/admin/assessments/attempts?${qs.stringify(
    {
      filter: {
        by_evaluations_ids: assessmentIds,
        by_enrollment_id: classroomEnrollmentId
      }
    },
    {
      arrayFormat: 'brackets'
    }
  )}`;

  return await getAPI().get(assessmentUrl);
};

const fetchAssignmentsAttempts = async (
  assignmentIds: string[],
  classroomEnrollmentId: string
) => {
  const assignmentUrl = `/admin/assignment_attempts?${qs.stringify(
    {
      filter: {
        by_evaluations_ids: assignmentIds,
        by_enrollment_id: classroomEnrollmentId
      }
    },
    {
      arrayFormat: 'brackets'
    }
  )}`;

  return await getAPI().get(assignmentUrl);
};

const getEvaluationsKind = async (
  evaluation: IClassroomEvaluation
): Promise<EvaluationKind> => {
  if (evaluation.evaluation_type === 'Assessment') {
    const assessment: AxiosResponse<IAssessment> = await getAPI().get(
      `admin/assessments/${evaluation.evaluation_id}`
    );

    return assessment.data.kind;
  }

  return 'assignment';
};

export const mountEvaluationsData = (
  evaluations: IClassroomEvaluation[],
  assessmentAttempts: IAssessmentAttempt[],
  assignmentAttempts: IAssignmentAttempt[]
): Promise<IEvaluationsWithAttemptsData[]> => {
  const evaluationsDataPromises = evaluations.map(async evaluation => {
    const attempts =
      evaluation.evaluation_type === 'Assessment'
        ? assessmentAttempts.filter(
            attempt => attempt.evaluation_id === evaluation.id
          )
        : assignmentAttempts.filter(
            attempt => attempt.evaluation_id === evaluation.id
          );

    const score = getAttemptsHighScore(attempts);

    const evaluationKind =
      evaluation.evaluation_type === 'Assessment'
        ? attempts.length > 0
          ? (attempts[0] as IAssessmentAttempt).assessment.kind
          : await getEvaluationsKind(evaluation)
        : 'assignment';

    return {
      ...evaluation,
      score,
      attempts,
      evaluationKind
    };
  });

  return Promise.all(evaluationsDataPromises);
};

const getEvaluationsIdsByAttemptType = (
  evaluations: IClassroomEvaluation[]
) => {
  const assessmentIds: string[] = [];
  const assignmentIds: string[] = [];

  for (const evaluation of evaluations) {
    if (evaluation.evaluation_type === 'Assessment') {
      assessmentIds.push(evaluation.id);
    } else {
      assignmentIds.push(evaluation.id);
    }
  }

  return {
    assessmentIds,
    assignmentIds
  };
};

const getAttemptsHighScore = (
  attempts: (IAssessmentAttempt | IAssignmentAttempt)[]
) => {
  const scores = attempts.map(score => score.score);
  return Math.max(...scores);
};
